import React from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Inbox,
  Building2,
  Users,
  Kanban,
  CheckSquare,
  Activity,
  Mail,
  Settings,
  FileText,
  PanelLeftClose,
  PanelLeftOpen,
  Shield,
  BadgeCheck,
} from "lucide-react";
import AppBrandMark from "../branding/AppBrandMark";
import { cn } from "../../lib/utils";
import { useAuth } from "../../lib/auth-context";
import { canAccessPublicForms, canSeeMailAudit } from "../../lib/capabilities";
import { canSeeConsentCenter, canSeeEmailLog, canSeeFormsNavigation } from "../../lib/crm-permissions";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, key: "dashboard" },
  { to: "/leads", label: "Leads", icon: Inbox, key: "leads" },
  { to: "/firmen", label: "Firmen", icon: Building2, key: "firmen" },
  { to: "/kontakte", label: "Kontakte", icon: Users, key: "kontakte" },
  { to: "/pipeline", label: "Pipeline", icon: Kanban, key: "pipeline" },
  { to: "/aufgaben", label: "Aufgaben", icon: CheckSquare, key: "aufgaben" },
  { to: "/aktivitaeten", label: "Aktivitäten", icon: Activity, key: "aktivitaeten" },
];

const SETTINGS_NAV = [
  { to: "/einstellungen", label: "Einstellungen", icon: Settings, key: "einstellungen" },
];

export default function Sidebar({ collapsed, setCollapsed, mobileOpen, setMobileOpen }) {
  const { isSuperAdmin, role, capabilities } = useAuth();
  const effectiveCollapsed = collapsed && !mobileOpen;
  const widthClass = mobileOpen
    ? "w-[216px] max-w-[78vw]"
    : collapsed
      ? "w-0 md:w-[64px]"
      : "w-0 md:w-[268px]";
  let workspaceNav = [...NAV];
  if (canSeeEmailLog({ role, isSuperAdmin }) && canSeeMailAudit(capabilities)) {
    workspaceNav.push({ to: "/emails", label: "E-Mail-Protokoll", icon: Mail, key: "emails" });
  }
  let settingsNav = isSuperAdmin
    ? [
        ...SETTINGS_NAV,
        { to: "/admin/tenants", label: "Mandanten", icon: Shield, key: "admin-tenants" },
      ]
    : role === "TENANT_ADMIN"
      ? [
          { to: "/team", label: "Team", icon: Users, key: "team" },
          ...SETTINGS_NAV,
        ]
      : SETTINGS_NAV;
  if (canSeeConsentCenter({ role, isSuperAdmin })) {
    settingsNav = [
      ...settingsNav,
      { to: "/einwilligungen", label: "Einwilligungen", icon: BadgeCheck, key: "consents" },
    ];
  }
  if (canSeeFormsNavigation({ role, isSuperAdmin }) && canAccessPublicForms(capabilities)) {
    settingsNav = [
      ...settingsNav.slice(0, isSuperAdmin ? 1 : role === "TENANT_ADMIN" ? 1 : 0),
      { to: "/formulare", label: "Formulare", icon: FileText, key: "formulare" },
      ...settingsNav.slice(isSuperAdmin ? 1 : role === "TENANT_ADMIN" ? 1 : 0),
    ];
  }

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/70 backdrop-blur-sm md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden
        />
      )}

      <aside
        data-testid="sidebar"
        className={cn(
          "z-50 shrink-0 flex flex-col",
          "border-r border-border bg-sidebar text-sidebar-foreground",
          "transition-[width] duration-200",
          "fixed md:static inset-y-0 left-0",
          widthClass,
          "overflow-hidden",
        )}
      >
        {/* Brand header */}
        <div className="h-16 shrink-0 flex items-center px-4 border-b border-border">
          <div data-testid="sidebar-brand" className="flex items-center min-w-0 max-w-full">
            <AppBrandMark
              showText={!effectiveCollapsed}
              containerClassName="flex items-center gap-2.5 min-w-0 max-w-full"
              badgeClassName="h-9 w-9"
              iconClassName="h-4 w-4"
              textClassName="text-sm font-display font-semibold tracking-tight truncate"
              imageClassName={
                effectiveCollapsed
                  ? "block h-auto max-h-9 w-auto max-w-full object-contain"
                  : "block h-auto max-h-9 w-auto max-w-[180px] object-contain"
              }
            />
          </div>
        </div>

        <button
          type="button"
          data-testid="sidebar-collapse-toggle"
          onClick={() => {
            if (mobileOpen) {
              setMobileOpen(false);
              return;
            }
            setCollapsed((c) => !c);
          }}
          className={cn(
            "mx-3 mt-3 mb-2 flex items-center gap-2 rounded-lg px-3 py-2 text-sm",
            "text-muted-foreground hover:bg-muted hover:text-foreground btn-tactile",
          )}
        >
          {effectiveCollapsed ? (
            <PanelLeftOpen className="w-4 h-4 shrink-0" />
          ) : (
            <PanelLeftClose className="w-4 h-4 shrink-0" />
          )}
          {!effectiveCollapsed && <span>Einklappen</span>}
        </button>

        <nav data-testid="sidebar-nav" className="min-h-0 flex-1 overflow-y-auto p-3 space-y-0.5">
          {!effectiveCollapsed && (
            <div className="px-2.5 pb-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80">
              Workspace
            </div>
          )}
          {workspaceNav.map((it) => (
            <NavItem
              key={it.key}
              item={it}
              collapsed={effectiveCollapsed}
              onClick={() => setMobileOpen(false)}
            />
          ))}
          <div className="mt-3 mb-2 h-px bg-border" />
          {!effectiveCollapsed && (
            <div className="px-2.5 pt-3 pb-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80">
              Verwaltung
            </div>
          )}
          {settingsNav.map((it) => (
            <NavItem
              key={it.key}
              item={it}
              collapsed={effectiveCollapsed}
              onClick={() => setMobileOpen(false)}
            />
          ))}
        </nav>

      </aside>
    </>
  );
}

function NavItem({ item, collapsed, onClick }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      data-testid={`sidebar-nav-item-${item.key}`}
      className={({ isActive }) =>
        cn(
          "group relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors",
          isActive
            ? "font-semibold text-foreground"
            : "text-muted-foreground hover:bg-muted hover:text-foreground",
        )
      }
      style={({ isActive }) =>
        isActive
          ? {
              background: "color-mix(in srgb, var(--brand) 12%, transparent)",
              boxShadow: "inset 0 0 0 1px color-mix(in srgb, var(--brand) 25%, transparent)",
            }
          : undefined
      }
    >
      {({ isActive }) => (
        <>
          {/* Active indicator */}
          <span
            aria-hidden
            className={cn(
              "absolute left-0 top-1/2 -translate-y-1/2 h-5 w-0.5 rounded-r-full transition-opacity",
              isActive ? "opacity-100" : "opacity-0",
            )}
            style={{ background: "var(--brand)" }}
          />
          <Icon
            className="w-4 h-4 shrink-0"
            style={isActive ? { color: "var(--brand)" } : undefined}
          />
          {!collapsed && <span className="truncate">{item.label}</span>}
        </>
      )}
    </NavLink>
  );
}
